import React, { useState, useEffect, useRef } from "react";

export default function MarkerEditModal({
  marker,
  onSave,
  onClose,
  showDev,
  onAddTempMarker,
  isTemp,
}) {
  const [team, setTeam] = useState(marker.team || "");
  const [logo, setLogo] = useState(marker.logo || "");
  const [markerName, setMarkerName] = useState(marker.markerName || "");
  const [tier, setTier] = useState(marker.tier || "");
  const [slot, setSlot] = useState(marker.slot || "");
  // Extract just the filename from the full flag path
  const extractFlagFilename = (fullPath) => {
    if (!fullPath) return "";
    const parts = fullPath.split('/');
    return parts[parts.length - 1] || "";
  };
  const [flag, setFlag] = useState(extractFlagFilename(marker.flag));
  const fileInputRef = useRef(null);

  useEffect(() => {
    setMarkerName(marker.markerName || "");
    setLogo(marker.logo || "");
    setTeam(marker.team || "");
    setTier(marker.tier || "");
    setFlag(extractFlagFilename(marker.flag));
    setSlot(marker.slot || "");
  }, [marker.markerName, marker.id, marker.logo, marker.team, marker.tier, marker.flag, marker.slot]);

  // Handle file upload and convert to data URL
  const handleLogoFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.type !== "image/png" && file.type !== "image/jpeg") {
      alert("Only .png and .jpeg images are allowed!");
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      setLogo(ev.target.result);
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveLogo = (e) => {
    e.preventDefault();
    setLogo("");
  };

  // Global flag folder selection
  const FLAG_FOLDER_KEY = "lootspot_flag_folder";
  const [flagFolder, setFlagFolder] = useState(() => {
    return localStorage.getItem(FLAG_FOLDER_KEY) || "VP-Flags";
  });
  useEffect(() => {
    localStorage.setItem(FLAG_FOLDER_KEY, flagFolder);
  }, [flagFolder]);

  // Helper to get available flags from selected folder
  function getAvailableFlags() {
    if (flagFolder === "Kowo-Flags") {
      return [
        "",
        "1-CAN.png","2-POL.png","3-FIN.png","4-DEN.png","5-BRA.png","6-GER.png","7-FRA.png","8-UK.png","9-SWE.png","10-NOR.png","11-SPA.png","12-AU.png","13-TR.png","14-USA.png","15-POR.png","16-ARG.png","17-KOR.png","18-JP.png","19-AUS.png","20-CN.png","21-CRO.png","22-GR.png","23-UA.png"
      ];
    } else {
      return [
        "",
        "1-CAN.png","2-ITA.png","3-FIN.png","4-IND.png","5-BRA.png","6-GER.png","7-FRA.png","8-GBR.png","9-SWE.png","10-JAM.png","11-ESP.png","12-AUT.png","13-TUR.png","14-USA.png","15-POR.png","16-ARG.png","17-KOR.png","18-JPN.png","19-AUS.png","20-CHN.png","21-CRO.png","22-GRE.png"
      ];
    }
  }

  // Helper to get full flag path
  function getFlagPath(flagFile) {
    if (!flagFile) return "";
    return `/${flagFolder}/${flagFile}`;
  }

  const handleSave = (e) => {
    e.preventDefault();
    const flagPath = getFlagPath(flag);
    // ZABRANA: Ako marker ima contest marker, ne dozvoli spremanje na backend
    if (!isTemp) {
      // Pretpostavljamo da je dostupan window.__allMarkers ili window.markers (ili proslijediti markers kao prop)
      // Ako nije, možeš koristiti workaround s window.markers
      const allMarkers = window.__allMarkers || window.markers || [];
      if (allMarkers.some((m) => m.id === marker.id + "_contest1")) {
        // alert('Ne možeš spremiti marker dok je u contestu! Prvo makni contest marker.');
        return; // tiho ignoriraj spremanje
      }
    }
    if (isTemp) {
      onSave(
        {
          ...marker,
          team,
          logo,
          markerName: markerName.trim() || marker.markerName || "",
          tier: tier || "",
          flag: flagPath,
          slot: slot || "",
        },
        showDev,
      );
      return;
    }
    if (showDev) {
      if (!markerName.trim()) {
        alert("Marker name is required!");
        return;
      }
      onSave(
        {
          ...marker,
          team,
          logo,
          markerName: markerName.trim(),
          tier: tier || "",
          flag: flagPath,
          slot: slot || "",
        },
        showDev,
      );
    } else {
      // Obični user: ne šalji na backend
      onSave(
        {
          ...marker,
          team,
          logo,
          markerName: marker.markerName || "",
          tier: tier || "",
          flag: flagPath,
          slot: slot || "",
        },
        false,
      );
    }
  };

  // Klik na bluranu pozadinu zatvara modal
  function handleBackdropClick(e) {
    // Zatvori samo ako je kliknuto direktno na backdrop (ne na modal)
    if (e.target === e.currentTarget) {
      if (onClose) onClose();
    }
  }

  return (
    <div className="animate-fadein fixed inset-0 z-50 flex items-center justify-center bg-ewave-dark/50 backdrop-blur-md" onClick={handleBackdropClick}>
      <div className="relative w-[420px] rounded-md border-2 border-ewave-border bg-ewave-dark p-8 text-center">
        <h2 className="mb-2 text-2xl font-bold uppercase text-white">
          {isTemp ? "Edit temp marker" : "Edit marker"}
        </h2>
        <form onSubmit={handleSave}>
          {/* Always allow marker name editing for temp marker, otherwise only for devs */}
          {(isTemp || showDev) && (
            <>
              <label className="mb-2 block font-semibold text-[#60a5fa]">Marker name:</label>
              <input
                className="mb-4 w-full rounded border border-primary bg-ewave-darker p-2 text-primary placeholder-primary focus:ring-2 focus:ring-primary"
                value={markerName}
                onChange={(e) => setMarkerName(e.target.value)}
                placeholder="Enter marker name"
                required
              />
            </>
          )}
          <label className="mb-2 block font-semibold uppercase tracking-wide text-primary">
            Team name:
          </label>
          <input
            className="placeholder-tertiary mb-4 w-full rounded border border-ewave-border bg-ewave-darker p-2 text-tertiary focus:ring-2 focus:ring-primary"
            value={team}
            onChange={(e) => setTeam(e.target.value)}
            placeholder="Enter team name"
          />
          <label className="mb-2 block font-semibold uppercase tracking-wide text-primary">
            Tier:
          </label>
          <input
            className="placeholder-tertiary mb-4 w-full rounded border border-ewave-border bg-ewave-darker p-2 text-tertiary focus:ring-2 focus:ring-primary"
            value={tier}
            onChange={(e) => setTier(e.target.value)}
            placeholder="Pro, Event, 1, 2, 3, 4, 5"
          />
          <label className="mb-2 block font-semibold uppercase tracking-wide text-primary">
            Logo (image):
          </label>
          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png, image/jpeg"
            onChange={handleLogoFile}
            className="hidden"
          />
          {/* Custom styled button */}
          {!logo && (
            <button
              type="button"
              className="mb-4 flex w-full cursor-pointer items-center justify-center gap-2 rounded border-2 border-dashed border-primary bg-ewave-darker p-4 text-base font-semibold text-tertiary"
              onClick={() => fileInputRef.current?.click()}
            >
              <span className="text-xl font-bold">+</span>
              Upload Logo
            </button>
          )}
          {logo && (
            <div className="mb-4 flex flex-col items-center">
              <img
                src={logo}
                alt="logo preview"
                className="mb-2 h-16 w-16 rounded-full border-2 border-ewave-border object-cover"
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  className="cursor-pointer rounded-md border border-ewave-border bg-ewave-darker px-4 py-1 text-sm font-semibold text-white hover:bg-ewave-hover"
                  onClick={() => fileInputRef.current?.click()}
                >
                  Change
                </button>
                <button
                  type="button"
                  className="cursor-pointer rounded-md border border-ewave-border bg-ewave-darker px-4 py-1 text-sm font-semibold text-white hover:bg-ewave-hover"
                  onClick={handleRemoveLogo}
                >
                  Remove
                </button>
              </div>
            </div>
          )}
          <div className="mb-4">
            <label className="block mb-1 text-primary font-semibold">Flag Select:</label>
            <select
              className="w-full rounded border border-ewave-border bg-ewave-darker p-2 text-white"
              value={flagFolder}
              onChange={e => setFlagFolder(e.target.value)}
            >
              <option value="VP-Flags">VP-Flags</option>
              <option value="Kowo-Flags">Kowo-Flags</option>
            </select>
          </div>
          <div className="mb-4">
            <label className="block mb-1 text-primary font-semibold">Flag:</label>
            <select
              className="w-full rounded border border-ewave-border bg-ewave-darker p-2 text-white"
              value={flag}
              onChange={e => setFlag(e.target.value)}
            >
              {getAvailableFlags().map((f) => (
                <option key={f} value={f}>
                  {f === "" ? "No flag" : f}
                </option>
              ))}
            </select>
            {flag && (
              <img
                src={`/${flagFolder}/${flag}`}
                alt="Flag preview"
                style={{ marginTop: 8, width: 48, height: 32, border: "1px solid #444", borderRadius: 4 }}
              />
            )}
          </div>
          {/* Add Contest Team button for non-contest markers */}
          {typeof marker.id === "string" && !marker.id.includes("_contest") && onAddTempMarker && (
            <button
              type="button"
              className="mb-0 mt-2 w-full rounded border border-ewave-border bg-ewave-darker px-4 py-2 font-semibold text-white hover:bg-ewave-hover"
              onClick={(e) => {
                e.preventDefault();
                if (typeof window !== "undefined" && window.onAddContestMarker)
                  window.onAddContestMarker(marker);
                if (typeof onAddTempMarker === "function") onAddTempMarker("contest", marker);
              }}
            >
              Add Contest Team
            </button>
          )}
          <button
            type="submit"
            className="mt-2 w-full rounded border border-ewave-border bg-ewave-darker px-4 py-2 font-semibold text-white hover:bg-ewave-hover"
          >
            Save
          </button>
          <button
            type="button"
            className="mt-2 w-full rounded border border-ewave-border bg-ewave-darker px-4 py-2 font-semibold text-white hover:bg-ewave-hover"
            onClick={onClose}
          >
            Close
          </button>
        </form>
      </div>
    </div>
  );
}
